import { Layout, Spin, Alert, Grid } from "antd";
import { useState, useEffect } from "react";
import bgImage from "../assets/yacht_bg.png";
import CommonHeader from "../components/common/CommonHeader";
import CommonFooter from "../components/common/CommonFooter";
import HeroSection from "../components/home/HeroSection";
import ListingsSection from "../components/home/ListingsSection";
import BottomSection from "../components/home/BottomSection";
import { firebaseService } from "../lib/services/firebaseService";
import { Property } from "../types/propert";

const { useBreakpoint } = Grid;

const HomePage = () => {
  const [allProperties, setAllProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    country: "",
    city: "",
    propertyType: "",
    priceRange: "",
  });
  const screens = useBreakpoint();

  useEffect(() => {
    // Real-time listener for approved properties only
    const unsubscribe = firebaseService.getApprovedProperties(
      (propertiesData) => {
        // Transform to Property instances
        const transformedListings = propertiesData.map(
          (propertyData) => new Property(propertyData)
        );

        setAllProperties(transformedListings);
        setFilteredProperties(transformedListings); // Initially show all
        setLoading(false);
        setError(null);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Apply filters whenever filters change
  useEffect(() => {
    if (allProperties.length === 0) return;

    let filtered = [...allProperties];

    // Apply country filter
    if (filters.country) {
      filtered = filtered.filter((property) =>
        property.country?.toLowerCase().includes(filters.country.toLowerCase())
      );
    }

    // Apply city filter
    if (filters.city) {
      filtered = filtered.filter((property) =>
        property.city?.toLowerCase().includes(filters.city.toLowerCase())
      );
    }

    // Apply property type filter
    if (filters.propertyType && filters.propertyType !== "ALL") {
      filtered = filtered.filter(
        (property) =>
          property.propertyType?.toLowerCase() ===
          filters.propertyType.toLowerCase()
      );
    }

    // Apply price range filter
    if (filters.priceRange) {
      const [minPrice, maxPrice] = filters.priceRange
        .split("-")
        .map((price) => {
          if (price.endsWith("M")) {
            return parseFloat(price) * 1000000; // Convert millions to actual number
          }
          return parseFloat(price);
        });

      filtered = filtered.filter((property) => {
        const price = property.finalPrice || property.listPrice || 0;
        return price >= minPrice && price <= maxPrice;
      });
    }

    setFilteredProperties(filtered);
  }, [filters, allProperties]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      country: "",
      city: "",
      propertyType: "",
      priceRange: "",
    });
    setFilteredProperties(allProperties);
  };

  if (loading) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          background: `url(${bgImage}) no-repeat center center/cover`,
          color: "white",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <CommonHeader circularMenu={true} />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <Spin size="large" />
        </div>
        <CommonFooter showInWhite={true} />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout
        style={{
          minHeight: "100vh",
          background: `url(${bgImage}) no-repeat center center/cover`,
          color: "white",
          fontFamily: "Poppins, sans-serif",
        }}
      >
        <CommonHeader circularMenu={true} />
        <div style={{ padding: "20px" }}>
          <Alert message="Error" description={error} type="error" showIcon />
        </div>
        <CommonFooter showInWhite={true} />
      </Layout>
    );
  }

  return (
    <Layout
      style={{
        minHeight: "100vh",
        background: `url(${bgImage}) no-repeat center center/cover`,
        color: "white",
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <CommonHeader circularMenu={true} />
      <HeroSection
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
        isMobile={!screens.md}
      />
      <div style={{ marginTop: -60 }}>
        <ListingsSection
          listings={filteredProperties}
          totalCount={allProperties.length}
          filteredCount={filteredProperties.length}
          isMobile={!screens.md}
        />
      </div>
      <BottomSection isMobile={!screens.md} />
      <CommonFooter showInWhite={true} />
    </Layout>
  );
};

export default HomePage;
